import { Fragment, useLayoutEffect, useState } from 'react'

import Button from 'components/common/Button/Button'
import Dropdown from 'components/common/Dropdown/Dropdown'
import Icon from 'components/common/Icon/Icon'
import { IconType } from 'constants/assets'
import { Language } from 'translations/Language'
import { QueryStatus } from 'components/common/QueryStatus'
import { timezoneNames } from 'utils/time'
import { useChangeLocale } from 'dataQueries'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @component User Preferences displayed in Profile page
 * @example
 * <Preferences userData={userMeRequest.data} />
 */
export const Preferences: React.FC = () => {
  const { t } = useTranslation(['profile', 'language'])

  const { baseUserData } = useUserData()

  const changeLocale = useChangeLocale()

  const [currentLanguage, setCurrentLanguage] = useState<Language>()
  const [currentTimezone, setCurrentTimezone] = useState<string>()

  // Reset mutation state on mount/unmount
  useLayoutEffect(() => {
    changeLocale.reset()
    return () => {
      changeLocale.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    if (baseUserData?.language) setCurrentLanguage(Language[(baseUserData.language as Language)])
  }, [baseUserData])

  useLayoutEffect(() => {
    if (baseUserData?.defaultTimezone) setCurrentTimezone((baseUserData.defaultTimezone))
  }, [baseUserData])

  return (
    <Fragment>
      <h2 className="decorated">{t('preferences.title')}</h2>
      <div className="section align-start">
        <div className="input-group">
          <label htmlFor="language">{t('language.label')}</label>
          <Dropdown button={(isOpen, action) => (
            <Button type="secondary" onClick={action} textAndIcon={true}>
              <span>{(t(`language:${(currentLanguage || '').toUpperCase()}`) || '').toString()}</span>
              <Icon icon={IconType.CARET_DOWN} className={`caret ${isOpen ? 'up' : 'down'}`} />
            </Button>
          )}>
            <Fragment>
              {Object.values(Language).map(lang => {
                const selected = lang === currentLanguage
                return (
                  <button
                    key={lang}
                    className={`language-button ${selected ? 'selected' : ''}`}
                    onClick={() => {
                      if (!baseUserData?.email) return
                      setCurrentLanguage(lang)
                      changeLocale.mutate({ email: baseUserData.email, language: lang })
                    }}
                  >
                    {t(`language:${lang.toUpperCase()}`, lang)}
                  </button>
                )
              })}
            </Fragment>
          </Dropdown>
        </div>
        <div className="input-group">
          <label htmlFor="timezone">{t('timezone.label')}</label>
          <select
            className="timezone-select"
            name="timezone"
            id="timezone"
            value={currentTimezone}
            onChange={e => {
              if (!baseUserData?.email) return
              setCurrentTimezone(e.target.value)
              changeLocale.mutate({ email: baseUserData.email, timezone: e.target.value })
            }}
          >
            {timezoneNames.map(tzn => (
              <option value={tzn} key={tzn}>{tzn}</option>
            ))}
          </select>
        </div>
      </div>
      <QueryStatus
        query={changeLocale}
        successMessage={t('preferences.success')}
        spaceTopRem={2}
      />
    </Fragment>
  )
}
